import Link from "next/link";
import Image from "next/image";
import type { BlogPost } from "@/lib/blog";
import { getFieldMeta, formatPostDate } from "@/lib/blog";

const GRADIENTS: Record<string, string> = {
  violet: "from-violet-500 via-fuchsia-500 to-pink-500",
  blue: "from-blue-500 via-indigo-500 to-purple-500",
  cyan: "from-cyan-500 via-blue-500 to-emerald-500",
  amber: "from-amber-500 via-orange-500 to-rose-500",
};

export function postSlug(post: BlogPost): string {
  return post.slug;
}

export default function PostCard({ post }: { post: BlogPost }) {
  const field = getFieldMeta(post.field);
  const gradient = GRADIENTS[post.cover_gradient] || GRADIENTS.violet;

  return (
    <Link
      href={`/blog/${post.slug}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white/80 backdrop-blur-sm transition hover:-translate-y-1 hover:shadow-xl dark:border-slate-800 dark:bg-[#0f172a]/80"
    >
      <div className={`relative flex h-36 items-center justify-center overflow-hidden bg-gradient-to-br ${gradient} text-4xl`}>
        {post.cover_image_url ? (
          <Image src={post.cover_image_url} alt={post.title} fill className="object-cover" unoptimized sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" />
        ) : (
          field.icon
        )}
      </div>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center gap-2 text-xs">
          <span className="rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            {field.icon} {field.label}
          </span>
          <span className="text-slate-400 dark:text-slate-500">{post.read_time_minutes} min read</span>
        </div>
        <h3 className="mt-3 line-clamp-2 text-lg font-semibold text-slate-900 dark:text-white">{post.title}</h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm text-slate-600 dark:text-slate-400">{post.excerpt}</p>
        <p className="mt-4 text-xs text-slate-400 dark:text-slate-500">{formatPostDate(post)}</p>
      </div>
    </Link>
  );
}
